// External dependencies
import {useEffect, useState} from '@wordpress/element';
import {useSelect, useDispatch, dispatch, select} from '@wordpress/data';
import {extensionCartUpdate} from '@woocommerce/blocks-checkout'
import {Button, Spinner, StoreNotice} from '@woocommerce/blocks-components';
import {Skeleton} from '@woocommerce/blocks-components';
import {cartStore} from '@woocommerce/block-data';
import {__} from '@wordpress/i18n';
import {hasQuoteShippingRateSelected} from "../utilities";
import {getSetting} from "@woocommerce/settings";
import {Popup} from "./popup";
import {attributes} from '../contact-for-shipping-quote/attributes';

// Field validation on submit
const useValidationFunction = (shippingAddress, billingAddress, requestedQuote) => {
	const {setValidationErrors, clearValidationError} = useDispatch(
		'wc/store/validation'
	);
	const requiredFields = getSetting('wccsq/required-fields', []);

	useEffect(() => {
		if (!requestedQuote) return;

		requiredFields.forEach(requiredField => {
			const isEmail = requiredField === 'email';
			const value = isEmail ? billingAddress.email : shippingAddress[requiredField];

			if (value.length === 0) {
				const errorId = isEmail ? 'billing_email' : `shipping_${requiredField}`;
				const message = isEmail ? __('Email is required.') : `${requiredField.replace('_', ' ')} is required.`;

				setValidationErrors({
					[errorId]: {message, hidden: false},
				});
			}
		})

		return () => {
			clearValidationError('shipping_first_name');
		};
	}, [
		billingAddress,
		shippingAddress,
		requestedQuote,
		clearValidationError,
		setValidationErrors,
	]);
}

const Block = ({label, quoteRequestedText, refreshLabel}) => {
	const [requestedQuote, setRequestedQuote] = useState(false)
	const [waitingForProcessing, setWaitingForProcessing] = useState(false);
	const [showPopup, setShowPopup] = useState(false);

	const {
		cfsqData,
		descriptionData,
		shippingRates,
		billingAddress,
		shippingAddress,
		isUpdating,
		shippingRateBeingSelected,
		popup,
	} = useSelect((select) => {
		const store = select(cartStore);
		const cartData = store.getCartData();

		return {
			cfsqData: cartData?.extensions?.['contact-for-shipping-quote'] ?? false,
			descriptionData: cartData?.extensions?.['contact-for-shipping-quote']?.['shipping-quote-request-description'] ?? false,
			shippingRates: cartData?.shippingRates ?? null,
			billingAddress: cartData?.billingAddress ?? null,
			shippingAddress: cartData?.shippingAddress ?? null,
			isUpdating: store.isResolving('getCartData') || !store.hasFinishedResolution('getCartData') || !store.getHasCalculatedShipping() || store.isCustomerDataUpdating(),
			shippingRateBeingSelected: store.isShippingRateBeingSelected() ?? false,
			popup: cartData?.extensions?.['contact-for-shipping-quote']?.['popup'] ?? false,
		}
	})

	// Required fields validation
	useValidationFunction(shippingAddress, billingAddress, requestedQuote);

	// Show popup for new quote request
	useEffect(() => {
		setShowPopup(requestedQuote && cfsqData.quote?.status === 'new');
	}, [requestedQuote, cfsqData.quote]);

	// When a new shipping rate is being selected, the current 'requested' status is removed.
	// This prevents the popup from showing when requesting the quote and switching between options.
	useEffect(() => {
		setRequestedQuote(false);
	}, [shippingRateBeingSelected])

	// Handle submission of quote request
	const onSubmit = (a) => {
		setRequestedQuote(true);
		setWaitingForProcessing(true);

		// Send a separate API request - or does this add the data to the cart update?
		extensionCartUpdate({
			namespace: 'contact-for-shipping-quote',
			data: {
				action: 'request-shipping-quote',
			}
		}).then((a, b) => {
			setWaitingForProcessing(false); // Spinner
			dispatch(cartStore).invalidateResolutionForStoreSelector('getCartData') // Refresh order button
		// }).catch((e) => {

		})
	}

	const onRefresh = (e) => {
		e.preventDefault();
		dispatch(cartStore).invalidateResolutionForStoreSelector('getCartData')
	}

	return (
		<>
			{ // Spinner
				hasQuoteShippingRateSelected(shippingRates) &&
				shippingRateBeingSelected && (
					window?.wc?.blocksComponents?.Skeleton ? (
						<Skeleton height="51px" width="177px"/>
					) : (
						<div style={{position: 'relative', width: '50px'}}>
							<Spinner/>
						</div>
					)
				)
			}

			{ // Request quote button
				!isUpdating &&
				!shippingRateBeingSelected &&
				cfsqData.quote?.status !== 'new' && cfsqData.quote?.status !== 'pending' &&
				descriptionData?.request_link &&
				(
					<Button
						className='wc-block-components-checkout-request-shipping-quote-button'
						onClick={onSubmit}
						disabled={waitingForProcessing}
					>
						{waitingForProcessing && <Spinner/>}

						<div
							className='wc-block-components-checkout-request-shipping-quote-button__text'
						>
							{label || attributes.requestQuoteButtonLabel.default}
						</div>
					</Button>
				)
			}

			{ // Notice
				(cfsqData.quote?.status === 'new') &&
				(
					<StoreNotice status="success" isDismissible={false}>
						{quoteRequestedText || attributes.quoteRequestedText.default}
						&nbsp;<a href="#" onClick={onRefresh}>{refreshLabel || attributes.refreshLabel.default}</a>
					</StoreNotice>
				)
			}

			<Popup show={showPopup} popupPageId={popup}></Popup>
		</>
	);
};
export const RequestQuoteButton = Block;
